import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Avatar,
  IconButton,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
  Fade,
  Tooltip,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  PhotoCamera as CameraIcon,
  Key as KeyIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import authService, { RegisterData } from '../../services/authService';

// Interface para os dados do formulário
interface FormData {
  name: string;
  email: string;
  cpf:string;
}

type FormErrors = Partial<Record<'name' | 'email' | 'cpf' | 'password' | 'confirmPassword', string>>;

const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estado inicial do formulário
  const [formData, setFormData] = useState<RegisterData>({
    name: '',
    email: '',
    password: '',
    role: 'user',
    status: 'active',
    sendPasswordResetEmail: 1,
    cpf:''
  });

  // Estados para validação
  const [errors, setErrors] = useState<FormErrors>({});

  // Carregar dados do usuário para 
useEffect(() => {
  const loadUser = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const response = await authService.getUserById(parseFloat(id));
      const user = response;

      console.log(user)
      setFormData({
        name: user.name || '',
        email: user.email || '',
        cpf: user.cpf || '',
        role: user.role || 'user',
        status: user.status || 'active',
        password: '',
        sendPasswordResetEmail: 1
      });

      if (user.imagem) {
        setAvatarPreview(user.imagem);
      }

    } catch (err) {
      console.error(err)
      setError('Erro ao carregar dados do usuário');
    } finally {
      setLoading(false);
    }
  };

  if (isEditMode) {
    loadUser();
  }
}, [id, isEditMode]);
  // Manipulador para alterações em campos de texto
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  // Manipulador para alterações no select
  const handleSelectChange = (event: SelectChangeEvent) => {
    const name = event.target.name as keyof FormData;
    const value = event.target.value;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Manipulador para o switch de redefinição de senha
  // const handleResetEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setFormData({
  //     ...formData,
  //     sendPasswordResetEmail: event.target.checked,
  //   });
  // };

  // Manipulador para upload de avatar
  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setFormData({
        ...formData,
        imagem: file,
      });
      
      // Criar URL para preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validação do formulário
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}; 
    
    if (!formData.name.trim()) {
      newErrors.name = 'O nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'O e-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
 
    setErrors(newErrors);   // Validar senha apenas em modo de criação ou se uma senha foi fornecida

    return Object.keys(newErrors).length === 0;
  };

  // Envio do formulário
 const handleSubmit = async (event: React.FormEvent) => {
  event.preventDefault();

  if (!validateForm()) return;

  // Validação de senha: obrigatória no cadastro, opcional na edição
  const pwd = (formData.password || '').trim();
  if (!isEditMode) {
    if (!pwd) {
      setErrors((prev) => ({ ...prev, password: 'A senha é obrigatória' }));
      return;
    }
    if (pwd.length < 6) {
      setErrors((prev) => ({ ...prev, password: 'Mínimo de 6 caracteres' }));
      return;
    }
    if (pwd !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: 'As senhas não conferem' }));
      return;
    }
  } else if (pwd) {
    if (pwd.length < 6) {
      setErrors((prev) => ({ ...prev, password: 'Mínimo de 6 caracteres' }));
      return;
    }
    if (pwd !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: 'As senhas não conferem' }));
      return;
    }
  }

  setSavingData(true);
  setError(null);
  setSuccess(null);

  try {
    let retorno;
    const previousRole = formData.role;

    if (isEditMode && id) {
      // Editar usuário
      retorno = await authService.updateUser(parseFloat(id), formData);
      // Atualizar senha (se preenchida)
      if (pwd) {
        await authService.updatePassword(parseFloat(id), { password: pwd });
      }
    } else {
      // Criar usuário
      retorno = await authService.register(formData);
    }

    console.log(retorno)
    
    if (retorno.status === 200 || retorno.status === 201) {
      setSuccess(isEditMode ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
      // Se o usuário editado for o mesmo do token atual, a role usada nos guards/menus
      // só é buscada uma vez no Layout. Então forçamos refresh para refletir a nova permissão.
      if (isEditMode && id) {
        try {
          const me = await authService.me();
          const meId = me?.user?.id;
          const editedId = Number(id);
          const newRole = retorno?.data?.user?.role ?? retorno?.data?.user?.perfil;

          if (meId && meId === editedId && newRole && newRole !== previousRole) {
            navigate('/'); // evita ficar em rota bloqueada
            window.location.reload();
            return;
          }
        } catch {
          // Se falhar, segue fluxo normal.
        }
      }

      navigate('/usuarios');
    } else {
      setError('Erro ao salvar usuário. Tente novamente.');
    }

  } catch (err) {
    console.error(err);
    setError('Não foi possível salvar os dados do usuário. Tente novamente.');
  } finally {
    setSavingData(false);
  }
};

  const handleBack = () => {
    navigate('/usuarios');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{ borderRadius: '8px' }}
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {isEditMode ? 'Editar Usuário' : 'Novo Usuário'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: '8px' }}>
            {success}
          </Alert>
        )}

        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mb: 4,
          }}
        >
          <Grid container spacing={3}>
            {/* Seção de avatar */}
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={avatarPreview || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  mb: 2,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '3rem',
                }}
              >
                {!avatarPreview && (formData.name.charAt(0) || <PersonIcon sx={{ fontSize: 60 }} />)}
              </Avatar>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload">
                <Button 
                  component="span" 
                  variant="outlined" 
                  startIcon={<CameraIcon />}
                  sx={{ borderRadius: '8px' }}
                >
                  Alterar Foto
                </Button>
              </label>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Informações básicas */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                Informações Básicas
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>


            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-mail"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
                required
                type="email"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cpf"
                name="cpf"
                value={formData.cpf}
                onChange={handleInputChange}
                error={!!errors.cpf}
                helperText={errors.cpf}
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                <InputLabel id="role-label">Papel</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleSelectChange}
                  label="Papel"
                >
                  <MenuItem value="user">Usuário</MenuItem>
                  <MenuItem value="viewer">Visualizador</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </Select>
                <FormHelperText>Determina as permissões no sistema</FormHelperText>
              </FormControl>
            </Grid>



          

         
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  label="Status"
                >
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                </Select>
                <FormHelperText>Usuários inativos não podem acessar o sistema</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Seção de senha */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  Senha
                </Typography>
                {isEditMode && (
                  <Tooltip title="A senha atual não é exibida por segurança. Preencha apenas se quiser alterar.">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                      <KeyIcon fontSize="small" />
                      <Typography variant="body2">Alterar senha</Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={isEditMode ? 'Nova senha' : 'Senha'}
                name="password"
                value={formData.password || ''}
                onChange={handleInputChange}
                error={!!errors.password}
                helperText={errors.password || (isEditMode ? 'Deixe em branco para manter a senha atual' : 'Mínimo de 6 caracteres')}
                required={!isEditMode}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirmar senha"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                }}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required={!isEditMode}
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end">
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{ borderRadius: '8px' }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={savingData ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={savingData}
                sx={{
                  py: 1.2,
                  px: 3,
                  borderRadius: '8px',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                }}
              >
                {savingData ? 'Salvando...' : 'Salvar'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </div>
    </Fade>
  );
};

export default UserForm; 